import React, { useState } from 'react';
import { useClassName } from 'common/hooks';
import { ContentfulRichText } from 'common/components';
import { CollapsibleInfoItem } from 'public/components/CollapsibleInfoItem';
import { ControlledTabs } from 'public/components/Tabs';
import useCustomStyle from '../../hooks';

import './faqs.less';

const tagLabel = (tag) =>
  tag
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export default function Faqs({ component }) {
  const [, style] = useCustomStyle(component.fields.settings);
  const pageSize = parseInt(style['--page-size']) || 7;
  const [showAll, setShowAll] = useState(false);
  const [openFaq, setOpenFaq] = useState(-1);
  const [activeTag, setActiveTag] = useState(null);
  const classNames = useClassName('CLPFaqs');

  const handleShowAnswer = (index) => {
    if (index !== openFaq) return setOpenFaq(index);
    setOpenFaq(-1);
  };

  // Card variant: same collapsible cards as the checkout "Other information"
  // block, one open at a time, no "Show more" (e.g. bundle More information).
  // Items tagged (keyValue.tags) collapse into a single "FAQs" card with
  // pill tabs filtering by tag; untagged items render as before. This keeps
  // every other LP (no tagged items) rendering unchanged.
  if (style['--variant'] === 'card') {
    const faqs = component.fields.faqs;
    const taggedFaqs = faqs.filter((faq) => faq.fields.tags?.length);
    const tags = [...new Set(taggedFaqs.flatMap((faq) => faq.fields.tags))];
    const activeTab = activeTag || tags[0];
    const visibleTaggedFaqs = taggedFaqs.filter((faq) =>
      faq.fields.tags.includes(activeTab)
    );
    let renderedTaggedGroup = false;

    return (
      <div className={classNames('cards')}>
        {faqs.map((faq, index) => {
          if (faq.fields.tags?.length) {
            if (renderedTaggedGroup) return null;
            renderedTaggedGroup = true;

            return (
              <CollapsibleInfoItem
                key={`faq-group-${index}`}
                title="FAQs"
                visible={openFaq === index}
                onToggle={() => handleShowAnswer(index)}>
                <ControlledTabs
                  names={tags.map(tagLabel)}
                  selectedIndex={tags.indexOf(activeTab)}
                  onClick={(name) =>
                    setActiveTag(tags[tags.map(tagLabel).indexOf(name)])
                  }
                />
                {visibleTaggedFaqs.map((item) => (
                  <div key={item.sys.id} className={classNames('tabbed-faq')}>
                    <p className={classNames('tabbed-faq-question')}>
                      {item.fields.key}
                    </p>
                    <ContentfulRichText
                      field={{ 'en-US': item.fields.richTextValue }}
                    />
                  </div>
                ))}
              </CollapsibleInfoItem>
            );
          }

          return (
            <CollapsibleInfoItem
              key={`faq-${index}`}
              title={faq.fields.key}
              visible={openFaq === index}
              onToggle={() => handleShowAnswer(index)}>
              <ContentfulRichText
                field={{ 'en-US': faq.fields.richTextValue }}
              />
            </CollapsibleInfoItem>
          );
        })}
      </div>
    );
  }

  const faqsToShow = showAll
    ? component.fields.faqs
    : component.fields.faqs.slice(0, pageSize);

  return (
    <div
      className={classNames(['container', component.fields.theme || 'light'])}>
      <div className={classNames('content')}>
        <div className={classNames('title')}>{component.fields.title}</div>
        <div className={classNames('description')}>
          <ContentfulRichText
            field={{ 'en-US': component.fields.description }}
          />
        </div>
      </div>
      <div className={classNames('faqs', showAll ? 'expanded' : 'collapsed')}>
        {faqsToShow.map((faq, index) => (
          <div
            key={`faq-${index}`}
            className={classNames(
              'faq-container',
              openFaq === index ? 'open' : 'closed'
            )}
            onClick={() => handleShowAnswer(index)}>
            <div className={classNames('faq')}>
              <p className={classNames('faq-text')}>{faq.fields.key}</p>
              <button
                onClick={() => handleShowAnswer(index)}
                className={classNames(
                  'show-btn',
                  component.fields.theme || 'light'
                )}>
                {openFaq === index ? '-' : '+'}
              </button>
            </div>
            <div
              className={classNames(
                'answer',
                openFaq === index ? 'visible' : 'hidden'
              )}>
              <ContentfulRichText
                field={{ 'en-US': faq.fields.richTextValue }}
              />
            </div>
          </div>
        ))}
        <button
          onClick={() => setShowAll((prev) => !prev)}
          className={classNames(
            'show-more-btn',
            component.fields.theme || 'light'
          )}>
          Show {showAll ? 'less' : 'more'}
        </button>
      </div>
    </div>
  );
}
